import { useCallback, useEffect, useRef, useState } from "react";
import type { MouseEvent as ReactMouseEvent } from "react";

export interface FloatingTooltipState {
  text: string;
  x: number;
  y: number;
}

interface UseFloatingTooltipOptions {
  timeoutMs?: number;
}

export function useFloatingTooltip({ timeoutMs = 3000 }: UseFloatingTooltipOptions = {}) {
  const [tooltip, setTooltip] = useState<FloatingTooltipState | null>(null);
  const timeoutRef = useRef<number | null>(null);

  const clearTooltipTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const hide = useCallback(() => {
    setTooltip(null);
    clearTooltipTimeout();
  }, [clearTooltipTimeout]);

  const showFromEvent = useCallback((event: ReactMouseEvent, text: string) => {
    event.stopPropagation();
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    setTooltip({
      text,
      x: rect.left + rect.width / 2,
      y: rect.top - 10,
    });

    clearTooltipTimeout();
    timeoutRef.current = window.setTimeout(() => {
      setTooltip(null);
    }, timeoutMs);
  }, [clearTooltipTimeout, timeoutMs]);

  useEffect(() => {
    const handleClickOutside = () => {
      hide();
    };

    if (tooltip) {
      document.addEventListener("click", handleClickOutside);
      return () => {
        document.removeEventListener("click", handleClickOutside);
      };
    }
  }, [hide, tooltip]);

  useEffect(() => {
    return () => {
      clearTooltipTimeout();
    };
  }, [clearTooltipTimeout]);

  return {
    tooltip,
    showFromEvent,
    hide,
  };
}
